/* eslint-disable max-lines */
import { DataModel, ID } from './data.model';
import {
  ActionCreator,
  ActionReducer,
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  props,
  ReducerManager,
  Store,
} from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

export interface EntityStoreActions<Model extends DataModel> {
  selectData(): Observable<Model[]>;

  selectActiveData(): Observable<Model[]>;

  selectIsLoading(): Observable<boolean>;

  selectError(): Observable<any>;

  addActiveItemById(id: ID): void;

  deselectItemById(id: ID): void;

  deselectAll(): void;

  toggleItemById(id: ID): void;

  setItems(items: Model[]): void;

  setIsLoading(isLoading: boolean): void;

  setError(err): void;

  removeItemById(id: ID): void;

  addItem(item: Model): void;

  updateItem(id: ID, item: Model): void;

  hasEntity(id: ID): boolean;

  selectEntity(id: ID): Observable<Model>;

  setActiveItems(selectedIds: ID[]): void;

  clearData(): void;

  /**
   * get ids of items, marked as selected
   *
   * @returns array of ids of selected items
   */
  getSelectedIds(): Observable<ID[]>;

  getAllItems(): Observable<Model[]>;
}

export class BaseStore<Model extends DataModel>
  implements EntityStoreActions<Model>
{
  actionsMap: EntityStoreActionsMap;
  selectorsMap: EntityStoreSelectorsMap<Model>;

  protected dataName: string;

  constructor(
    dataName: string,
    protected store: Store,
  ) {
    this.dataName = dataName;
    this.actionsMap =
      EntityStoreFactory.getEntityStoreActionsMap<Model>(dataName);
    this.selectorsMap =
      EntityStoreFactory.getEntityStoreSelectorsMap<Model>(dataName);
  }

  addItem(item: Model): void {
    this.store.dispatch(this.actionsMap.addItem({ item }));
  }

  clearData(): void {
    this.store.dispatch(this.actionsMap.clearAll());
  }

  deselectAll(): void {
    this.store.dispatch(this.actionsMap.deactivateAllItems());
  }

  deselectItemById(id: ID): void {
    this.store.dispatch(this.actionsMap.deactivateItem({ id }));
  }

  getAllItems(): Observable<Model[]> {
    // TODO refactor: return observable
    return this.store.select(this.selectorsMap.selectAll).pipe(
      filter((x) => !!x),
      take(1),
      // to fix typedef issue, instead of "as Observable<Model[]>"
      map((items: Model[]) => items),
    );
  }

  getSelectedIds(): Observable<ID[]> {
    // TODO refactor: return observable
    return this.store.select(this.selectorsMap.selectAllActive).pipe(
      filter((x) => !!x),
      map((items: Model[]) => items.map((item) => item?.id)),
      take(1),
    );
  }

  hasEntity(id: ID): boolean {
    // TODO refactor: return observable
    let isExist: boolean;
    this.store
      .select(this.selectorsMap.selectAll)
      .pipe(
        map((items: Model[]) => {
          const item = items?.find((item) => {
            return item.id === id;
          });
          return item;
        }),
        take(1),
      )
      .subscribe((v: Model) => (isExist = !!v));
    return isExist;
  }

  removeItemById(id: ID): void {
    this.store.dispatch(this.actionsMap.deleteItem({ id }));
  }

  selectActiveData(): Observable<Model[]> {
    return this.store.select(this.selectorsMap.selectAllActive);
  }

  selectData(): Observable<Model[]> {
    return this.store.select(this.selectorsMap.selectAll);
  }

  selectEntity(id: ID): Observable<Model> {
    return this.store
      .select(this.selectorsMap.selectAll)
      .pipe(map((items: Model[]) => items.find((item) => item.id === id)));
  }

  selectError(): Observable<boolean> {
    return this.store.select(this.selectorsMap.selectError);
  }

  selectIsLoading(): Observable<boolean> {
    return this.store.select(this.selectorsMap.selectIsLoading);
  }

  addActiveItemById(id: ID): void {
    this.store.dispatch(this.actionsMap.activateItem({ id }));
  }

  setActiveItems(selectedIds: ID[]): void {
    this.store.dispatch(this.actionsMap.setActiveItems({ ids: selectedIds }));
  }

  setError(err): void {
    this.store.dispatch(this.actionsMap.setError({ error: err }));
  }

  setIsLoading(isLoading: boolean): void {
    this.store.dispatch(this.actionsMap.setLoading({ isLoading }));
  }

  setItems(items: Model[]): void {
    this.store.dispatch(this.actionsMap.setItems({ items }));
  }

  toggleItemById(id: ID): void {
    this.store.dispatch(this.actionsMap.toggleActive({ id }));
  }

  updateItem(id: ID, item: Model): void {
    this.store.dispatch(this.actionsMap.updateItem({ id, item }));
  }
}

enum EntityStoreActionsTypeDescriptions {
  LOAD_ITEMS = 'load all',
  ADD_ITEM = 'add one',
  SET_ITEMS = 'set items',
  TOGGLE_ACTIVE = 'toggle active',
  ACTIVATE_ALL_ITEMS = 'activate all',
  ACTIVATE_ITEM = 'activate one',
  SET_ACTIVE_ITEMS = 'set active items',
  DEACTIVATE_ALL_ITEMS = 'deselect all',
  DEACTIVATE_ITEM = 'deselect one',
  DELETE_ITEM = 'delete one',
  UPDATE_ITEM = 'update item',
  CLEAR_ALL = 'clear all',
  SET_LOADING = 'set isLoading',
  SET_ERROR = 'set error',
}

interface EntityStoreActionsDescription {
  setItems: string;
  addItem: string;
  deleteItem: string;
  activateItem: string;
  activateAllItems: string;
  setActiveItems: string;
  toggleActive: string;
  deactivateAllItems: string;
  deactivateItem: string;
  updateItem: string;
  clearAll: string;
  setError: string;
  setLoading: string;
}

type EntityStoreSelectorsList =
  | 'selectAll'
  | 'selectAllActive'
  | 'selectError'
  | 'selectIsLoading';
type EntityStoreSelectorsMap<Model extends DataModel> = Partial<
  Record<EntityStoreSelectorsList, any>
>;
type EntityStoreActionsMap = Partial<
  Record<keyof EntityStoreActionsDescription, ActionCreator<string, any>>
>;

function buildActionDescriptionHelper(
  dataName: string,
  type: EntityStoreActionsTypeDescriptions,
): string {
  return `[${dataName}] ${type}`;
}

interface EntityStoreState<Model extends DataModel> {
  data: Model[];
  selectedData: Model[];
  isLoading: boolean;
  error: any;
}

export class EntityStoreFactory {
  static create<Model extends DataModel>(
    dataName: string,
    store: Store,
    manager: ReducerManager,
  ): BaseStore<Model> {
    this.registerFeatureReducer(dataName, manager);
    return new BaseStore<Model>(dataName, store);
  }

  static getEntityStoreActionsMap<Model extends DataModel>(
    dataName: string,
  ): EntityStoreActionsMap {
    const actionTypes = this.getActionsTypeDescription(dataName);
    return {
      addItem: createAction(actionTypes.addItem, props<{ item: Model }>()),
      activateItem: createAction(actionTypes.activateItem, props<{ id: ID }>()),
      updateItem: createAction(
        actionTypes.updateItem,
        props<{ id: ID; item: Model }>(),
      ),
      toggleActive: createAction(actionTypes.toggleActive, props<{ id: ID }>()),
      setItems: createAction(actionTypes.setItems, props<{ items: Model[] }>()),
      setActiveItems: createAction(
        actionTypes.setActiveItems,
        props<{ ids: ID[] }>(),
      ),
      setLoading: createAction(
        actionTypes.setLoading,
        props<{ isLoading: boolean }>(),
      ),
      setError: createAction(actionTypes.setError, props<{ error: any }>()),
      activateAllItems: createAction(actionTypes.activateAllItems),
      deleteItem: createAction(actionTypes.deleteItem, props<{ id: ID }>()),
      deactivateItem: createAction(
        actionTypes.deactivateItem,
        props<{ id: ID }>(),
      ),
      deactivateAllItems: createAction(actionTypes.deactivateAllItems),
      clearAll: createAction(actionTypes.clearAll),
    };
  }

  static getEntityStoreSelectorsMap<Model extends DataModel>(
    dataName: string,
  ): EntityStoreSelectorsMap<Model> {
    const selectFeature = createFeatureSelector(dataName);
    return {
      selectAll: createSelector(
        selectFeature,
        (state: EntityStoreState<Model>) => state?.data || [],
      ),
      selectAllActive: createSelector(
        selectFeature,
        (state: EntityStoreState<Model>) => state?.selectedData || [],
      ),
      selectIsLoading: createSelector(
        selectFeature,
        (state: EntityStoreState<Model>) => state.isLoading,
      ),
      selectError: createSelector(
        selectFeature,
        (state: EntityStoreState<Model>) => state.error,
      ),
    };
  }

  private static getEntityStoreReducer(
    dataName: string,
  ): ActionReducer<unknown> {
    const actionsMap = this.getEntityStoreActionsMap(dataName);
    return createReducer(
      this.getInitialState(),
      on(actionsMap.addItem, (state, { item }) => ({
        ...state,
        data: [...state.data, item],
      })),
      on(actionsMap.setActiveItems, (state, { ids }) => {
        const toSelect = state.data.filter((item) => ids?.includes(item?.id));
        return { ...state, selectedData: [...toSelect] };
      }),
      on(actionsMap.activateItem, (state, { id }) => {
        if (state.selectedData.find((v) => v?.id === id)) {
          return state;
        } else {
          const item = state.data.find((v) => v?.id === id);
          return {
            ...state,
            selectedData: item
              ? [...state.selectedData, item]
              : state.selectedData,
          };
        }
      }),
      on(actionsMap.activateAllItems, (state) => ({
        ...state,
        selectedData: [...state.data],
      })),
      on(actionsMap.setItems, (state, { items }) => ({
        ...state,
        data: [...items],
      })),
      on(actionsMap.setLoading, (state, { isLoading }) => ({
        ...state,
        isLoading,
      })),
      on(actionsMap.setError, (state, { error }) => ({ ...state, error })),
      on(actionsMap.updateItem, (state, { id, item }) => {
        const index = state.data.findIndex((v) => v?.id === id);
        if (index !== -1) {
          const activeDataIndex = state.selectedData.findIndex(
            (v) => v?.id === id,
          );
          const activeDataClone = [...state.selectedData];
          if (activeDataIndex !== -1) {
            activeDataClone[index] = { ...item, id };
          }
          const dataClone = [...state.data];
          dataClone[index] = { ...item, id };
          return { ...state, data: dataClone, selectedData: activeDataClone };
        }
        return state;
      }),
      on(actionsMap.deleteItem, (state, { id }) => {
        const index = state.data.findIndex((v) => v?.id === id);
        const activeIndex = state.selectedData.findIndex((v) => v?.id === id);
        const dataClone = [...state.data];
        const activeDataClone = [...state.selectedData];
        if (index !== -1) {
          dataClone.splice(index, 1);
        }

        if (activeIndex !== -1) {
          activeDataClone.splice(index, 1);
        }
        return {
          ...state,
          data: [...dataClone],
          selectedData: [...activeDataClone],
        };
      }),
      on(actionsMap.clearAll, (state) => ({
        ...state,
        data: [],
        selectedData: [],
      })),
      on(actionsMap.deactivateAllItems, (state, {}) => ({
        ...state,
        selectedData: [],
      })),
      on(actionsMap.deactivateItem, (state, { id }) => {
        const index = state.selectedData.findIndex((v) => v?.id === id);
        const dataClone = [...state.selectedData];
        dataClone.splice(index, 1);
        return { ...state, selectedData: [...dataClone] };
      }),
      on(actionsMap.toggleActive, (state, { id }) => {
        const activeIndex = state.selectedData.findIndex((v) => v?.id === id);
        if (activeIndex !== -1) {
          const dataClone = [...state.selectedData];
          dataClone.splice(activeIndex, 1);
          return { ...state, selectedData: [...dataClone] };
        }
        const item = state.data.find((v) => v?.id === id);
        return { ...state, selectedData: [...state.selectedData, item] };
      }),
    );
  }

  private static getActionsTypeDescription(
    dataName: string,
  ): EntityStoreActionsDescription {
    return {
      addItem: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.ADD_ITEM,
      ),
      activateAllItems: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.ACTIVATE_ALL_ITEMS,
      ),
      activateItem: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.ACTIVATE_ITEM,
      ),
      toggleActive: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.TOGGLE_ACTIVE,
      ),
      deactivateAllItems: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.DEACTIVATE_ALL_ITEMS,
      ),
      deactivateItem: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.DEACTIVATE_ITEM,
      ),
      deleteItem: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.DELETE_ITEM,
      ),
      updateItem: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.UPDATE_ITEM,
      ),
      setActiveItems: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.SET_ACTIVE_ITEMS,
      ),
      setLoading: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.SET_LOADING,
      ),
      setItems: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.SET_ITEMS,
      ),
      setError: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.SET_ERROR,
      ),
      clearAll: buildActionDescriptionHelper(
        dataName,
        EntityStoreActionsTypeDescriptions.CLEAR_ALL,
      ),
    };
  }

  private static registerFeatureReducer(
    dataName: string,
    manager: ReducerManager,
  ): void {
    const reducer = this.getEntityStoreReducer(dataName);
    manager?.addReducer(dataName, reducer);
  }

  private static getInitialState<
    Model extends DataModel,
  >(): EntityStoreState<Model> {
    return {
      data: [],
      selectedData: [],
      isLoading: false,
      error: null,
    };
  }
}
